/* ============================================================================
 * NotificationBell — top-bar bell with an activity feed + unread badge.
 *
 * Surfaces client portal messages (unread), estimate signatures, and new leads
 * from lib/notificationsData. Polls every 60s so a new client message shows up
 * without a page reload ("like a text"). Admin-only (mounted gated in Topbar).
 * ========================================================================== */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, MessageSquare, PenLine, UserPlus } from 'lucide-react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover';
import {
  gatherNotifications,
  relativeTime,
  markAllSeen,
} from '@/lib/notificationsData';

const ICONS = {
  message: MessageSquare,
  signature: PenLine,
  lead: UserPlus,
};

const ICON_TONE = {
  message: 'text-blue-700 dark:text-blue-300',
  signature: 'text-[hsl(var(--primary))]',
  lead: 'text-emerald-700 dark:text-emerald-300',
};

const POLL_MS = 60000;

export const NotificationBell = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const timerRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const { items: next, unreadCount } = await gatherNotifications();
      setItems(next);
      setUnread(unreadCount);
    } catch {
      /* ignore — keep last known state */
    }
  }, []);

  useEffect(() => {
    refresh();
    timerRef.current = setInterval(refresh, POLL_MS);
    return () => clearInterval(timerRef.current);
  }, [refresh]);

  const handleOpenChange = (v) => {
    setOpen(v);
    if (v) {
      // Opening the bell clears the signature/lead unread high-water mark.
      // Unread client messages persist until read in the inbox.
      markAllSeen();
      refresh();
    }
  };

  const go = (item) => {
    setOpen(false);
    if (item.link) navigate(item.link);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={
            unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'
          }
          className="relative inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40 transition-colors"
          data-testid="notification-bell"
        >
          <Bell size={18} />
          {unread > 0 ? (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-[10px] font-bold leading-4 text-center"
              data-testid="notification-badge"
            >
              {unread > 9 ? '9+' : unread}
            </span>
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-80 p-0 bg-[hsl(var(--card))] border-border"
        data-testid="notification-panel"
      >
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
          <span className="text-xs uppercase tracking-[0.18em] font-semibold text-foreground">
            Notifications
          </span>
          {unread > 0 ? (
            <span className="text-[11px] text-muted-foreground">
              {unread} new
            </span>
          ) : null}
        </div>

        <div className="max-h-[22rem] overflow-y-auto">
          {items.length === 0 ? (
            <div
              className="px-4 py-10 text-center text-sm text-muted-foreground"
              data-testid="notification-empty"
            >
              <Bell
                size={22}
                className="mx-auto mb-2 text-muted-foreground/50"
              />
              You&apos;re all caught up.
            </div>
          ) : (
            items.map((it) => {
              const Icon = ICONS[it.kind] || Bell;
              return (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => go(it)}
                  className={`w-full flex items-start gap-3 px-4 py-3 text-left border-b border-border/60 hover:bg-[hsl(var(--muted))]/60 transition-colors ${
                    it.unread ? 'bg-[hsl(var(--primary))]/[0.04]' : ''
                  }`}
                  data-testid={`notification-item-${it.kind}`}
                >
                  <Icon
                    size={16}
                    className={`mt-0.5 shrink-0 ${ICON_TONE[it.kind] || 'text-muted-foreground'}`}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5">
                      <span className="text-sm font-semibold text-foreground truncate">
                        {it.title}
                      </span>
                      {it.count > 1 ? (
                        <span className="text-[10px] font-bold text-[hsl(var(--primary))]">
                          {it.count}
                        </span>
                      ) : null}
                    </span>
                    {it.body ? (
                      <span className="block text-xs text-muted-foreground truncate">
                        {it.body}
                      </span>
                    ) : null}
                    <span className="block text-[10px] uppercase tracking-[0.12em] text-muted-foreground/70 mt-0.5">
                      {relativeTime(it.at)}
                    </span>
                  </span>
                  {it.unread ? (
                    <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[hsl(var(--primary))]" />
                  ) : null}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
